const REGION_SEARCH_SEPARATOR_PATTERN = /[\\/|,\uFF0C>\uFF1E\s\u3000;\uFF1B\u3001]+/;
const REGION_SEARCH_REMOVE_PATTERN =
    /[\s\u3000\\/|,\uFF0C>\uFF1E;\uFF1B\u3001\u3002.!！?？:\uFF1A'"“”‘’`·~（）()[\]【】{}<>《》_-]+/g;
const REGION_DISPLAY_STATUS_SUFFIX_PATTERN = /\s*(?:（已停用）|\(已停用\))$/;

const REGION_NAME_SUFFIXES = [
    '社区居民管理委员会',
    '社区居民委员会',
    '新疆维吾尔自治区',
    '广西壮族自治区',
    '宁夏回族自治区',
    '内蒙古自治区',
    '西藏自治区',
    '村民委员会',
    '居民委员会',
    '街道办事处',
    '社区委员会',
    '社区居委会',
    '嘎查委员会',
    '牧民委员会',
    '街道委员会',
    '社区服务中心',
    '村民委会',
    '居民委会',
    '村委员会',
    '村委会',
    '居委会',
    '委员会',
    '特别行政区',
    '自治州',
    '自治县',
    '自治旗',
    '经济技术开发区',
    '高新技术产业开发区',
    '高新技术开发区',
    '经济开发区',
    '开发区',
    '新区',
    '林区',
    '地区',
    '街道',
    '民族乡',
    '牧委会',
    '家委会',
    '生产队',
    '村委',
    '居委',
    '委会',
    '社区',
    '嘎查',
    '苏木',
    '乡',
    '镇',
    '村',
    '盟',
    '旗',
    '省',
    '市',
    '区',
    '县',
].sort((left, right) => right.length - left.length);

const REGION_SUFFIX_EQUIVALENTS: Record<string, string[]> = {
    村民委员会: ['村委会', '村委员会', '村民委会', '村'],
    村委会: ['村民委员会', '村委员会', '村民委会', '村'],
    村委员会: ['村民委员会', '村委会', '村民委会', '村'],
    村民委会: ['村民委员会', '村委会', '村委员会', '村'],
    居民委员会: ['居委会', '居民委会', '居委'],
    居委会: ['居民委员会', '居民委会', '居委'],
    居民委会: ['居民委员会', '居委会', '居委'],
    社区居民管理委员会: ['社区居民委员会', '社区居委会', '社区委员会', '社区', '居民委员会', '居委会'],
    社区居民委员会: ['社区居民管理委员会', '社区居委会', '社区委员会', '社区', '居民委员会', '居委会'],
    社区居委会: ['社区居民委员会', '社区委员会', '社区', '居委会'],
    社区委员会: ['社区居民委员会', '社区居委会', '社区'],
    社区服务中心: ['社区'],
    嘎查委员会: ['嘎查'],
    牧民委员会: ['牧委会'],
    牧委会: ['牧民委员会'],
    街道办事处: ['街道'],
    街道委员会: ['街道'],
};

export const splitRegionSearchText = (value: string): string[] =>
    value
        .trim()
        .split(REGION_SEARCH_SEPARATOR_PATTERN)
        .map((segment) => segment.trim())
        .filter(Boolean);

export const stripRegionDisplayStatusText = (value: string): string =>
    value.replace(REGION_DISPLAY_STATUS_SUFFIX_PATTERN, '').trim();

export const normalizeRegionSearchText = (value?: string | null): string =>
    (value || '')
        .trim()
        .normalize('NFKC')
        .toLocaleLowerCase()
        .replace(REGION_SEARCH_REMOVE_PATTERN, '');

const stripRegionSuffixOnce = (value: string): string => {
    for (const suffix of REGION_NAME_SUFFIXES) {
        if (value.endsWith(suffix) && value.length > suffix.length) {
            return value.slice(0, -suffix.length);
        }
    }
    return value;
};

const getEquivalentRegionNameAliases = (value: string): string[] => {
    for (const suffix of REGION_NAME_SUFFIXES) {
        if (!value.endsWith(suffix) || value.length <= suffix.length) {
            continue;
        }

        const base = value.slice(0, -suffix.length);
        const baseVariants = [base];
        if (base.endsWith('村')) {
            baseVariants.push(base.slice(0, -1));
        }
        if (base.endsWith('社区')) {
            baseVariants.push(base.slice(0, -2));
        }
        const uniqueBaseVariants = Array.from(new Set(baseVariants.filter(Boolean)));

        return [
            ...uniqueBaseVariants.map((baseVariant) => `${baseVariant}${suffix}`),
            ...(REGION_SUFFIX_EQUIVALENTS[suffix] ?? []).flatMap((equivalentSuffix) =>
                uniqueBaseVariants.map((baseVariant) => `${baseVariant}${equivalentSuffix}`),
            ),
        ];
    }

    return [];
};

export const getRegionNameSearchAliases = (name: string): string[] => {
    const aliases: string[] = [];
    const seen = new Set<string>();
    let current = name.normalize('NFKC').trim();

    while (current) {
        const normalized = normalizeRegionSearchText(current);
        if (normalized && !seen.has(normalized)) {
            aliases.push(normalized);
            seen.add(normalized);
        }

        getEquivalentRegionNameAliases(current).forEach((alias) => {
            const normalizedAlias = normalizeRegionSearchText(alias);
            if (normalizedAlias && !seen.has(normalizedAlias)) {
                aliases.push(normalizedAlias);
                seen.add(normalizedAlias);
            }
        });

        const stripped = stripRegionSuffixOnce(current);
        const normalizedStripped = normalizeRegionSearchText(stripped);
        if (stripped === current || normalizedStripped.length < 2) {
            break;
        }
        current = stripped;
    }

    return aliases;
};

export const buildRegionPathSearchAliases = (label: string): string[] => {
    const parts = splitRegionSearchText(stripRegionDisplayStatusText(label));
    const aliasGroups = parts
        .map(getRegionNameSearchAliases)
        .filter((aliases) => aliases.length > 0);
    const aliases = new Set<string>();

    const appendAliases = (groups: string[][], index: number, current: string) => {
        if (index >= groups.length) {
            if (current) {
                aliases.add(current);
            }
            return;
        }

        groups[index].forEach((alias) =>
            appendAliases(groups, index + 1, `${current}${alias}`),
        );
    };

    for (let start = 0; start < aliasGroups.length; start += 1) {
        appendAliases(aliasGroups.slice(start), 0, '');
    }

    return Array.from(aliases);
};

const matchesOrderedRegionAliases = (
    normalizedKeyword: string,
    regionPath: string,
): boolean => {
    const aliasGroups = splitRegionSearchText(stripRegionDisplayStatusText(regionPath))
        .map(getRegionNameSearchAliases)
        .filter((aliases) => aliases.length > 0);
    if (aliasGroups.length < 2) {
        return false;
    }

    const searchFrom = (
        keywordIndex: number,
        groupIndex: number,
        matchedCount: number,
    ): boolean => {
        if (keywordIndex >= normalizedKeyword.length) {
            return matchedCount >= 2;
        }
        if (groupIndex >= aliasGroups.length) {
            return false;
        }

        for (let currentGroupIndex = groupIndex; currentGroupIndex < aliasGroups.length; currentGroupIndex += 1) {
            for (const alias of aliasGroups[currentGroupIndex]) {
                if (normalizedKeyword.startsWith(alias, keywordIndex)) {
                    if (
                        searchFrom(
                            keywordIndex + alias.length,
                            currentGroupIndex + 1,
                            matchedCount + 1,
                        )
                    ) {
                        return true;
                    }
                }
            }
        }
        return false;
    };

    return searchFrom(0, 0, 0);
};

export const getRegionSearchTerms = (keyword: string): string[] =>
    Array.from(
        new Set(
            splitRegionSearchText(keyword)
                .map((segment) => normalizeRegionSearchText(segment))
                .filter(Boolean),
        ),
    );

export const matchesRegionPathKeyword = (
    regionPath: string,
    keyword: string,
    code?: string | null,
): boolean => {
    const normalizedKeyword = normalizeRegionSearchText(keyword);
    if (!normalizedKeyword) {
        return true;
    }

    const normalizedCode = normalizeRegionSearchText(code);
    if (normalizedCode.includes(normalizedKeyword)) {
        return true;
    }

    const searchableRegionPath = stripRegionDisplayStatusText(regionPath);
    const normalizedPath = normalizeRegionSearchText(regionPath);
    const normalizedSearchablePath = normalizeRegionSearchText(searchableRegionPath);
    if (
        normalizedPath.includes(normalizedKeyword) ||
        normalizedSearchablePath.includes(normalizedKeyword)
    ) {
        return true;
    }

    const pathAliases = buildRegionPathSearchAliases(searchableRegionPath);
    if (pathAliases.some((alias) => alias.includes(normalizedKeyword))) {
        return true;
    }
    if (matchesOrderedRegionAliases(normalizedKeyword, searchableRegionPath)) {
        return true;
    }

    const terms = getRegionSearchTerms(keyword);
    if (terms.length <= 1) {
        return false;
    }

    return terms.every(
        (term) =>
            normalizedPath.includes(term) ||
            normalizedSearchablePath.includes(term) ||
            normalizedCode.includes(term) ||
            pathAliases.some((alias) => alias.includes(term)),
    );
};

const getRegionSelectOptionLabelText = (value: unknown): string => {
    if (value === null || value === undefined || typeof value === 'boolean') {
        return '';
    }
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
    }
    if (Array.isArray(value)) {
        return value.map(getRegionSelectOptionLabelText).join('');
    }
    if (typeof value === 'object') {
        const maybeElement = value as { props?: { children?: unknown } };
        if (maybeElement.props && 'children' in maybeElement.props) {
            return getRegionSelectOptionLabelText(maybeElement.props.children);
        }
    }

    return '';
};

export interface RegionSelectFilterOption {
    label?: unknown;
    code?: string | null;
}

export const filterRegionSelectOption = (
    input: string,
    option?: RegionSelectFilterOption,
): boolean => {
    if (!input.trim()) {
        return true;
    }

    const label = getRegionSelectOptionLabelText(option?.label);
    return label ? matchesRegionPathKeyword(label, input, option?.code) : false;
};
